/**
 * Onboarding-demo data hooks.
 *
 * Follows the same useState/useEffect/useCallback pattern as
 * useVoiceSubscription.ts — wraps the placeholder API calls in
 * onboardingDemoApi.ts so consuming screens get a loading state instead of
 * reading a constant directly.
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  fetchQueueStatus,
  fetchPlanSizingBenchmarks,
  savePlanSizing,
  fetchTrialActivityExtras,
  fetchBillingActionLog,
  fetchNotChargedSummary,
  fetchAlertPreferences,
  type QueueStatus,
  type PlanSizingBenchmarks,
  type TrialActivityExtras,
  type BillingActionLogEntry,
  type NotChargedItem,
  type AlertPreference,
} from '@/services/onboardingDemoApi';
import type { VoiceSizingRequest } from '@/types/voice-subscription';

export function useQueueStatus(tenantId: number | null) {
  const [data, setData] = useState<QueueStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      setData(await fetchQueueStatus(tenantId));
    } catch {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading };
}

export function usePlanSizingBenchmarks(tenantId: number | null) {
  const [data, setData] = useState<PlanSizingBenchmarks | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      setData(await fetchPlanSizingBenchmarks(tenantId));
    } catch {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const save = useCallback(
    async (payload: VoiceSizingRequest) => {
      if (!tenantId) return;
      const next = await savePlanSizing(tenantId, payload);
      setData((prev) => ({
        ...next,
        avgSessionMinutes: prev?.avgSessionMinutes ?? null,
      }));
    },
    [tenantId]
  );

  return { data, isLoading, save, refetch: fetch };
}

export function useTrialActivityExtras(tenantId: number | null) {
  const [data, setData] = useState<TrialActivityExtras | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      setData(null);
      return;
    }
    setIsLoading(true);
    try {
      setData(await fetchTrialActivityExtras(tenantId));
    } catch {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading };
}

export function useBillingActionLog(tenantId: number | null, billingPeriod: string) {
  const [data, setData] = useState<BillingActionLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      setData(await fetchBillingActionLog(tenantId, billingPeriod));
    } catch {
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, billingPeriod]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading };
}

export function useNotChargedSummary(tenantId: number | null, billingPeriod: string) {
  const [data, setData] = useState<NotChargedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      setData(await fetchNotChargedSummary(tenantId, billingPeriod));
    } catch {
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, billingPeriod]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading };
}

export function useAlertPreferences(tenantId: number | null) {
  const [data, setData] = useState<AlertPreference[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      setData(await fetchAlertPreferences(tenantId));
    } catch {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading };
}
